import React from "react";

/**
 * Feature preview mockups — simple SVG/CSS visualizations that hint at each feature.
 * Fully brand-colored, no external images, no network dependencies.
 */
export function PreviewMock({ variant }) {
  if (variant === "income_target_ai") return <IncomeTargetMock />;
  if (variant === "track_existing") return <TrackExistingMock />;
  if (variant === "ai_balancing") return <AiBalancingMock />;
  if (variant === "referral_credits") return <ReferralMock />;
  if (variant === "ticker_research") return <TickerResearchMock />;
  if (variant === "income_calendar") return <IncomeCalendarMock />;
  if (variant === "watchlist_alerts") return <WatchlistAlertsMock />;
  if (variant === "team_stripe") return <TeamStripeMock />;
  if (variant === "broker_sync") return <BrokerSyncMock />;
  if (variant === "cross_account_rollup") return <CrossAccountMock />;
  if (variant === "install_and_alerts") return <InstallAndAlertsMock />;
  if (variant === "weekly_digest") return <WeeklyDigestMock />;
  if (variant === "portfolio_value_growth") return <PortfolioGrowthMock />;
  if (variant === "competitor_scorecard") return <CompetitorScorecardMock />;
  return null;
}

function PortfolioGrowthMock() {
  // Faux area chart — 30 daily data points trending up with a soft gradient.
  const pts = Array.from({ length: 30 }, (_, i) => {
    const y = 60 - i * 1.4 + Math.sin(i * 0.6) * 4;
    return `${i * (100 / 29)},${y}`;
  });
  const line = pts.join(" ");
  const fill = `0,60 ${line} 100,60`;
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Portfolio value over time</div>
          <div className="inline-flex gap-1 mono text-[8px] uppercase text-primary">
            <span className="px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">30D</span>
            <span className="px-1.5 py-0.5 rounded-full">90D</span>
            <span className="px-1.5 py-0.5 rounded-full">1Y</span>
          </div>
        </div>
        <div className="serif text-lg text-foreground">$27,133</div>
        <div className="mono text-[9px] text-primary">+$4,078 (+17.69%)</div>
        <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="flex-1 w-full h-full">
          <defs>
            <linearGradient id="pgm-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={fill} fill="url(#pgm-grad)" />
          <polyline points={line} fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
        </svg>
      </div>
    </div>
  );
}

function CompetitorScorecardMock() {
  const rows = [
    { name: "AI Optimizer", dpp: true, sn: false, sh: false },
    { name: "Portfolio grouping", dpp: true, sn: false, sh: false },
    { name: "30y backtest", dpp: true, sn: "Ltd", sh: false },
    { name: "Ex-div PUSH", dpp: true, sn: "Email", sh: "Email" },
    { name: "CSV export", dpp: true, sn: true, sh: true },
  ];
  const Tick = () => <span className="text-primary">✓</span>;
  const X = () => <span className="text-muted-foreground/50">×</span>;
  const cell = (v) =>
    v === true ? <Tick /> : v === false ? <X /> : <span className="mono text-[7px] text-muted-foreground">{v}</span>;
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-1.5">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">DPP vs competitors</div>
        <div className="grid grid-cols-[1fr,28px,28px,28px] gap-1 text-[8px] mono text-muted-foreground pb-1 border-b border-border">
          <div>Feature</div>
          <div className="text-center text-primary">DPP</div>
          <div className="text-center">Snow</div>
          <div className="text-center">Share</div>
        </div>
        {rows.map((r) => (
          <div key={r.name} className="grid grid-cols-[1fr,28px,28px,28px] gap-1 items-center text-[9px] py-0.5 border-b border-border/40 last:border-0">
            <div className="text-foreground/85 truncate pr-1">{r.name}</div>
            <div className="text-center">{cell(r.dpp)}</div>
            <div className="text-center">{cell(r.sn)}</div>
            <div className="text-center">{cell(r.sh)}</div>
          </div>
        ))}
        <div className="mono text-[8px] uppercase tracking-widest text-primary mt-auto">DPP wins 19 of 21</div>
      </div>
    </div>
  );
}

const wrap = "aspect-[16/10] w-full rounded-lg border border-border bg-muted/40 overflow-hidden relative";

function TrackExistingMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Fidelity · Live</div>
        {[
          { t: "SCHD", v: "$12,483", d: "+$212", pos: true },
          { t: "VYM", v: "$7,201", d: "+$91", pos: true },
          { t: "JEPI", v: "$3,915", d: "−$12", pos: false },
        ].map((r) => (
          <div key={r.t} className="flex items-center gap-2 rounded-md bg-card border border-border px-2 py-1.5">
            <span className="mono text-[10px] font-semibold w-9">{r.t}</span>
            <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary/60" style={{ width: `${{SCHD:70, VYM:45, JEPI:25}[r.t]}%` }} />
            </div>
            <span className="mono text-[10px] tabular-nums">{r.v}</span>
            <span className={`mono text-[9px] ${r.pos ? "text-primary" : "text-destructive"}`}>{r.d}</span>
          </div>
        ))}
        <div className="mono text-[9px] text-muted-foreground mt-auto">Read-only · No trading</div>
      </div>
    </div>
  );
}

function AiBalancingMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-widest text-secondary">Goal: Max income</div>
          <span className="mono text-[9px] text-muted-foreground">AI · Claude 4.6</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-md border border-border p-2">
            <div className="mono text-[8px] uppercase text-muted-foreground">Before</div>
            <div className="flex items-end gap-0.5 h-8 mt-1">
              {[40, 35, 25].map((h, i) => (
                <div key={i} className="flex-1 bg-muted-foreground/40 rounded-sm" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="mono text-[9px] mt-1">YoC 4.1%</div>
          </div>
          <div className="rounded-md border border-primary/40 bg-primary/5 p-2">
            <div className="mono text-[8px] uppercase text-primary">After</div>
            <div className="flex items-end gap-0.5 h-8 mt-1">
              {[70, 20, 10].map((h, i) => (
                <div key={i} className="flex-1 bg-primary/70 rounded-sm" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="mono text-[9px] mt-1 text-primary">YoC 6.8%</div>
          </div>
        </div>
        <div className="mono text-[9px] text-muted-foreground italic leading-snug mt-1">
          "Shift 30% from JEPI into SCHD to lift YoC by ~2.7%..."
        </div>
      </div>
    </div>
  );
}

function ReferralMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="mono text-[9px] uppercase tracking-widest text-secondary">Your link</div>
        <div className="rounded-md border border-border bg-card px-2 py-1.5 mono text-[9px] truncate">
          dividendportfolioplanner.com/auth?ref=<span className="text-secondary">7X4K9M2P</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-md border border-border p-2 text-center">
            <div className="serif text-xl">3</div>
            <div className="mono text-[8px] uppercase text-muted-foreground">Friends</div>
          </div>
          <div className="rounded-md border border-secondary/30 bg-secondary/5 p-2 text-center">
            <div className="serif text-xl text-secondary">90d</div>
            <div className="mono text-[8px] uppercase text-muted-foreground">Free earned</div>
          </div>
        </div>
        <div className="flex -space-x-1 mt-auto">
          {["A", "B", "C"].map((c, i) => (
            <div key={c} className={`w-5 h-5 rounded-full border border-card mono text-[9px] grid place-items-center ${i===0?"bg-primary text-primary-foreground":i===1?"bg-secondary text-secondary-foreground":"bg-muted-foreground/30"}`}>{c}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TickerResearchMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="mono text-sm font-semibold">KO</span>
          <span className="mono text-[9px] uppercase tracking-widest text-primary">Aristocrat</span>
        </div>
        <div className="mono text-[9px] text-muted-foreground -mt-1">The Coca-Cola Company</div>
        <div className="grid grid-cols-3 gap-1.5">
          <div className="rounded-md border border-border p-1.5">
            <div className="mono text-[8px] uppercase text-muted-foreground">Yield</div>
            <div className="mono text-[11px]">3.12%</div>
          </div>
          <div className="rounded-md border border-border p-1.5">
            <div className="mono text-[8px] uppercase text-muted-foreground">Payout</div>
            <div className="mono text-[11px]">68%</div>
          </div>
          <div className="rounded-md border border-secondary/30 bg-secondary/5 p-1.5">
            <div className="mono text-[8px] uppercase text-muted-foreground">Safety</div>
            <div className="mono text-[11px] text-secondary">A+</div>
          </div>
        </div>
        <div className="flex items-end gap-0.5 h-6 mt-auto">
          {[35, 40, 42, 45, 48, 52, 55, 58, 62, 65, 70, 74].map((h, i) => (
            <div key={i} className="flex-1 bg-primary/70 rounded-sm" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="mono text-[8px] uppercase text-muted-foreground">62 years of dividend growth</div>
      </div>
    </div>
  );
}

function IncomeCalendarMock() {
  // 12-month heatmap: darker = heavier dividend month. Quarterly payers (Mar/Jun/Sep/Dec)
  // spike; special monthly income holdings (JEPI) show consistent lighter payments.
  const monthly = [22, 8, 74, 12, 18, 88, 20, 14, 96, 18, 22, 108]; // stylized values
  const max = Math.max(...monthly);
  const months = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-widest text-secondary">Income · 2025</div>
          <div className="mono text-[9px] text-muted-foreground">Total $684</div>
        </div>
        <div className="grid grid-cols-12 gap-1 flex-1 items-end">
          {monthly.map((v, i) => {
            const alpha = 0.15 + (v / max) * 0.85;
            return (
              <div key={i} className="flex flex-col items-center gap-1">
                <div
                  className="w-full rounded-sm"
                  style={{
                    height: `${Math.max(6, (v / max) * 100)}%`,
                    backgroundColor: `rgba(15, 61, 50, ${alpha.toFixed(2)})`,
                  }}
                  title={`Month ${i + 1}: $${v}`}
                />
                <span className="mono text-[8px] text-muted-foreground">{months[i]}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between mono text-[8px] uppercase tracking-widest text-muted-foreground mt-1">
          <span>SCHD · VYM · JEPI</span>
          <span className="text-primary">$108 peak · Dec</span>
        </div>
      </div>
    </div>
  );
}


function WatchlistAlertsMock() {
  const alerts = [
    { tk: "SCHD", rule: "Price < $75", now: "$78.12", live: false },
    { tk: "JEPI", rule: "Yield > 8%",  now: "8.34%", live: true  },
    { tk: "O",    rule: "Price < $52", now: "$54.09", live: false },
  ];
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-widest text-secondary">Active alerts</div>
          <div className="mono text-[9px] text-muted-foreground">3 rules</div>
        </div>
        <div className="flex-1 space-y-1.5 overflow-hidden">
          {alerts.map((a) => (
            <div key={a.tk} className={`flex items-center justify-between rounded-sm px-2 py-1 text-[10px] border ${a.live ? "border-primary/30 bg-primary/[0.05]" : "border-border bg-card"}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="mono font-semibold">{a.tk}</span>
                <span className="text-muted-foreground truncate">{a.rule}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="mono">{a.now}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${a.live ? "bg-primary animate-pulse" : "bg-muted-foreground/30"}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mono text-[8px] uppercase tracking-widest text-muted-foreground">
          <span>Email + in-app</span>
          <span className="text-primary">1 triggered · JEPI</span>
        </div>
      </div>
    </div>
  );
}

function TeamStripeMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-widest text-secondary">Team billing</div>
          <span className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">Self-serve</span>
        </div>
        <div className="rounded-sm border border-border bg-card px-2 py-2">
          <div className="flex items-center justify-between text-[10px]">
            <span className="mono text-muted-foreground">Seats</span>
            <div className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full border border-border grid place-items-center">−</span>
              <span className="mono font-semibold">24</span>
              <span className="w-4 h-4 rounded-full border border-border grid place-items-center">+</span>
            </div>
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <span className="serif text-lg text-primary">$191.76</span>
            <span className="mono text-[8px] text-muted-foreground">/ month</span>
          </div>
          <div className="mt-1 h-1 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary" style={{ width: "58%" }} />
          </div>
          <div className="mono text-[8px] text-muted-foreground mt-1">14 of 24 seats consumed</div>
        </div>
        <div className="mt-auto flex items-center justify-between mono text-[8px] uppercase tracking-widest text-muted-foreground">
          <span>Stripe · pro_team_seat</span>
          <span className="text-primary">Grow anytime</span>
        </div>
      </div>
    </div>
  );
}


function IncomeTargetMock() {
  // Stylized "reverse solver" — user sets $1,500/mo target on the SCHD/JEPI/VYM
  // portfolio → AI proposes $258,120 upfront OR $1,850/mo contribution over 8y.
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="mono text-[9px] uppercase tracking-widest text-secondary">Income target</div>
          <span className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-secondary/15 text-secondary border border-secondary/30">AI</span>
        </div>

        {/* Target chip */}
        <div className="rounded-sm border border-secondary/40 bg-secondary/[0.06] px-2 py-1.5">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Goal</div>
          <div className="flex items-baseline justify-between mt-0.5">
            <span className="serif text-lg text-secondary">$1,500</span>
            <span className="mono text-[9px] text-muted-foreground">/ month · dividends</span>
          </div>
        </div>

        {/* Two solve paths */}
        <div className="grid grid-cols-2 gap-1.5">
          <div className="rounded-sm border border-primary/30 bg-primary/[0.05] px-2 py-1.5">
            <div className="mono text-[8px] uppercase tracking-widest text-primary">Lump-sum</div>
            <div className="serif text-sm text-primary mt-0.5">$258,120</div>
            <div className="mono text-[8px] text-muted-foreground">at 6.97% YoC</div>
          </div>
          <div className="rounded-sm border border-border bg-card px-2 py-1.5">
            <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Or monthly</div>
            <div className="serif text-sm">$1,850 / mo</div>
            <div className="mono text-[8px] text-muted-foreground">for 8 years · DRIP on</div>
          </div>
        </div>

        <div className="mono text-[8px] text-muted-foreground italic mt-auto leading-tight">
          Assumes 3% yield growth &amp; SCHD·JEPI·VYM 45/35/20 allocation.
        </div>
      </div>
    </div>
  );
}



function BrokerSyncMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">One-click link</div>
        <div className="rounded-md border border-border bg-card px-3 py-2 flex items-center justify-between">
          <div>
            <div className="mono text-xs font-semibold">Fidelity</div>
            <div className="mono text-[9px] text-muted-foreground">Roth IRA · read-only</div>
          </div>
          <div className="mono text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary uppercase tracking-widest">Linked</div>
        </div>
        <div className="rounded-md border border-border bg-card px-3 py-2 flex items-center justify-between">
          <div>
            <div className="mono text-xs font-semibold">Schwab</div>
            <div className="mono text-[9px] text-muted-foreground">Taxable</div>
          </div>
          <div className="mono text-[9px] px-1.5 py-0.5 rounded bg-secondary/15 text-secondary uppercase tracking-widest">Syncing…</div>
        </div>
        <div className="rounded-md border border-dashed border-border px-3 py-2 flex items-center justify-center gap-2 text-muted-foreground">
          <div className="mono text-[9px] uppercase tracking-widest">+ Connect a new broker</div>
        </div>
        <div className="mono text-[8px] text-muted-foreground italic mt-auto leading-tight">
          Powered by SnapTrade · DPP never places trades.
        </div>
      </div>
    </div>
  );
}

function CrossAccountMock() {
  const rows = [
    { name: "Roth IRA", type: "Fidelity", value: "$18,240", monthly: "$62" },
    { name: "Taxable", type: "Schwab", value: "$11,050", monthly: "$34" },
    { name: "401k", type: "Vanguard", value: "$74,900", monthly: "$210" },
  ];
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex flex-col gap-2">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">All accounts · rolled up</div>
        <div className="rounded-md bg-primary/[0.05] border border-primary/25 px-3 py-2 flex items-baseline justify-between">
          <div className="serif text-lg text-primary leading-none">$104,190</div>
          <div className="mono text-[9px] uppercase tracking-widest text-primary">$306/mo</div>
        </div>
        {rows.map((r) => (
          <div key={r.name} className="rounded-md border border-border bg-card px-3 py-1.5 flex items-center justify-between">
            <div>
              <div className="mono text-[10px] font-semibold">{r.name}</div>
              <div className="mono text-[9px] text-muted-foreground">{r.type}</div>
            </div>
            <div className="text-right">
              <div className="mono text-[10px]">{r.value}</div>
              <div className="mono text-[9px] text-secondary">{r.monthly}/mo</div>
            </div>
          </div>
        ))}
        <div className="mono text-[8px] text-muted-foreground italic mt-auto leading-tight">
          Toggle tax lots, filter by account type, export combined CSV.
        </div>
      </div>
    </div>
  );
}

function InstallAndAlertsMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 flex gap-3">
        {/* Phone home-screen with DPP tile */}
        <div className="w-1/2 rounded-xl border border-border bg-card p-2 flex flex-col">
          <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground text-center mb-1">Home screen</div>
          <div className="flex-1 grid grid-cols-3 gap-1.5 p-1">
            {["#DDE7E2","#F3EDE1","#DDE7E2","#F3EDE1","#0F3D32","#DDE7E2"].map((c, i) => (
              <div key={i} className={`aspect-square rounded-md ${i === 4 ? "ring-2 ring-primary" : ""}`} style={{ background: c }}>
                {i === 4 && (
                  <div className="w-full h-full grid place-items-center serif text-primary-foreground text-lg">D</div>
                )}
              </div>
            ))}
          </div>
          <div className="mono text-[8px] text-primary text-center leading-tight">DPP</div>
        </div>
        {/* Push notification */}
        <div className="w-1/2 flex flex-col gap-2 justify-center">
          <div className="mono text-[9px] uppercase tracking-widest text-secondary">Push · 2 min ago</div>
          <div className="rounded-md border border-primary/30 bg-primary/[0.06] px-2.5 py-2">
            <div className="mono text-[9px] font-semibold text-primary flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" /> Dividend Portfolio Planner
            </div>
            <div className="text-[10px] text-foreground mt-1 leading-tight">
              SCHD goes ex-dividend tomorrow — projected $34.20 on your 60 shares.
            </div>
          </div>
          <div className="rounded-md border border-border bg-card px-2.5 py-1.5 opacity-70">
            <div className="mono text-[9px] font-semibold text-secondary">JEPI ex-div Fri</div>
            <div className="mono text-[9px] text-muted-foreground">Projected $18.60</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WeeklyDigestMock() {
  return (
    <div className={wrap}>
      <div className="absolute inset-3 rounded-md bg-card border border-border p-3 flex flex-col gap-1.5 overflow-hidden">
        <div className="mono text-[8px] uppercase tracking-widest text-muted-foreground">From: The DPP Weekly · Sat 8:00am</div>
        <div className="serif text-sm text-primary leading-tight">Your Saturday dividend digest</div>
        <div className="mono text-[9px] text-secondary font-semibold pt-1">📊 Your holdings this week</div>
        <div className="mono text-[9px] text-foreground/80 leading-tight">Tracking 4 tickers: SCHD (60) · JEPI (30) · VYM (20) · O (15)</div>
        <div className="mono text-[9px] text-secondary font-semibold pt-1">Ex-dividend this week</div>
        <ul className="mono text-[9px] text-foreground/80 leading-tight space-y-0.5 pl-2">
          <li>· SCHD: ex-div in 2 days · projected $34.20</li>
          <li>· O: ex-div in 4 days · projected $3.60</li>
        </ul>
        <div className="mono text-[8px] text-muted-foreground italic mt-auto leading-tight">
          One-click unsubscribe in every email.
        </div>
      </div>
    </div>
  );
}

